import { getSearchTermsFromSearchValue } from 'lib/riskbase';
import {
  AssessmentSuggestions,
  Categories,
  Severities,
  Timescales,
} from './types';

export const returnAseFindingMatchingSearchTerm = <
  T extends
    | (AssessmentSuggestions['actions'][string] & {
        questionIds?: undefined;
      })
    | (AssessmentSuggestions['controls'][string] & {
        severityId?: undefined;
        timescaleId?: undefined;
        answerIds?: undefined;
      }),
>(
  findings: T[],
  searchTerm: string,
  {
    categories,
    timescales,
    severities,
  }: {
    categories: Categories | undefined;
    timescales: Timescales | undefined;
    severities: Severities | undefined;
  },
): T[] => {
  if (!findings.length) {
    return findings;
  }

  const {
    flagHasCategory,
    flagHasLink,
    flagHasSeverity,
    flagHasTimescale,
    searchTerms,
  } = getSearchTermsFromSearchValue(searchTerm).reduce<{
    flagHasCategory: boolean | undefined;
    flagHasSeverity: boolean | undefined;
    flagHasTimescale: boolean | undefined;
    flagHasLink: boolean | undefined;
    searchTerms: string[];
  }>(
    (acc, term) => {
      if (term) {
        if (term === '+category') {
          acc.flagHasCategory = true;
        } else if (term === '-category') {
          acc.flagHasCategory = false;
        } else if (term === '+severity') {
          acc.flagHasSeverity = true;
        } else if (term === '-severity') {
          acc.flagHasSeverity = false;
        } else if (term === '+timescale') {
          acc.flagHasTimescale = true;
        } else if (term === '-timescale') {
          acc.flagHasTimescale = false;
        } else if (term === '+link') {
          acc.flagHasLink = true;
        } else if (term === '-link') {
          acc.flagHasLink = false;
        } else {
          acc.searchTerms.push(term);
        }
      }
      return acc;
    },
    {
      flagHasCategory: undefined,
      flagHasSeverity: undefined,
      flagHasTimescale: undefined,
      flagHasLink: undefined,
      searchTerms: [],
    },
  );

  if (
    !searchTerms.length &&
    flagHasLink === undefined &&
    (categories === undefined || flagHasCategory === undefined) &&
    (severities === undefined || flagHasSeverity === undefined) &&
    (timescales === undefined || flagHasTimescale === undefined)
  ) {
    return findings;
  }

  return findings.reduce<T[]>((acc, finding) => {
    const searchString = [];

    if (flagHasLink !== undefined) {
      const hasLink =
        !!finding.negativeChecklistItemIds.length ||
        !!finding.positiveChecklistItemIds.length ||
        !!finding.answerIds?.length ||
        !!finding.questionIds?.length;
      if (flagHasLink ? !hasLink : hasLink) {
        return acc;
      }
    }

    if (categories) {
      const category = finding.categoryId
        ? categories[finding.categoryId]
        : undefined;
      if (category) {
        if (flagHasCategory === false) {
          return acc;
        }
        searchString.push(category.name);
      } else if (flagHasCategory === true) {
        return acc;
      }
    }

    if (severities) {
      const severity = finding.severityId
        ? severities[finding.severityId]
        : undefined;
      if (severity) {
        if (flagHasSeverity === false) {
          return acc;
        }
        searchString.push(severity.name);
      } else if (flagHasSeverity === true) {
        return acc;
      }
    }

    if (timescales) {
      const timescale = finding.timescaleId
        ? timescales[finding.timescaleId]
        : undefined;
      if (timescale) {
        if (flagHasTimescale === false) {
          return acc;
        }
        searchString.push(timescale.name);
      } else if (flagHasTimescale === true) {
        return acc;
      }
    }

    if (searchTerms.length) {
      if (finding.what) {
        searchString.push(finding.what);
      }
      if (finding.why) {
        searchString.push(finding.why);
      }
      const finalSearchString = searchString.join(' ').toLowerCase();
      if (
        !finalSearchString ||
        !searchTerms.every((term) => finalSearchString.includes(term))
      ) {
        return acc;
      }
    }

    acc.push(finding);
    return acc;
  }, []);
};
